import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Navbar, Footer } from '../components';
import { FaEye, FaClock, FaCalendar, FaMapMarkerAlt, FaTags, FaCog, FaPhone, FaArrowLeft, FaChevronLeft, FaChevronRight, FaWhatsapp } from 'react-icons/fa';
import './AnnouncementDetails.css';
import { useApp } from '../context/AppContext';
import { extractIdFromSlug, getAnnouncementUrl } from '../utils/slugify';
import { hasViewedAnnouncement, markAnnouncementAsViewed, hasFunctionalConsent } from '../utils/cookies';
import { getApiEndpoint, getUploadUrl } from '../config/api';

function AnnouncementDetails() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t, language } = useApp();
  const [announcement, setAnnouncement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showContactModal, setShowContactModal] = useState(false);
  const [imageTransitioning, setImageTransitioning] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);

  // Helper function to capitalize each word
  const capitalizeWords = (str) => {
    if (!str) return '';
    return str
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Helper function to format category path
  const formatCategoryPath = (category, subcategory, subsubcategory) => {
    const parts = [];
    
    if (category) {
      // Use translation if available, otherwise capitalize
      const categoryKey = category.toLowerCase().replace(/\s+/g, '-');
      const translatedCategory = t.categoryNames && t.categoryNames[categoryKey] 
        ? t.categoryNames[categoryKey] 
        : capitalizeWords(category);
      parts.push(translatedCategory);
    }
    
    if (subcategory && subcategory !== category) {
      const subcategoryKey = subcategory.toLowerCase().replace(/\s+/g, '-');
      const translatedSubcategory = t.categoryNames && t.categoryNames[subcategoryKey]
        ? t.categoryNames[subcategoryKey]
        : capitalizeWords(subcategory);
      parts.push(translatedSubcategory);
    }
    
    if (subsubcategory && subsubcategory !== subcategory) {
      const translatedSubsubcategory = t.subsubCategories && t.subsubCategories[subsubcategory]
        ? t.subsubCategories[subsubcategory]
        : capitalizeWords(subsubcategory);
      parts.push(translatedSubsubcategory);
    }
    
    return parts.join(' > ');
  };

  const fetchAnnouncementDetails = async () => {
    try {
      // Extract ID from the full URL path
      const id = extractIdFromSlug(location.pathname);
      
      if (!id) {
        throw new Error('Invalid announcement URL');
      }
      
      // Check if user has consent for functional cookies
      const hasConsent = hasFunctionalConsent();
      const alreadyViewed = hasConsent ? hasViewedAnnouncement(id) : false;
      
      // Skip view increment if:
      // 1. User has no consent (privacy requirement), OR
      // 2. User has consent AND already viewed this announcement
      const shouldSkip = !hasConsent || alreadyViewed;
      const url = getApiEndpoint(`/api/announcements/${id}${shouldSkip ? '?skipViewIncrement=true' : ''}`);
      
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error('Announcement not found');
      }
      const data = await response.json();
            // API returns { success: true, announcement: {...} }
      if (data.success && data.announcement) {
        setAnnouncement(data.announcement);
        
        // Mark as viewed in cookies ONLY if user has consent AND it's a new view
        if (hasConsent && !alreadyViewed) {
          markAnnouncementAsViewed(id);
        }
      } else {
        setAnnouncement(data);
      }
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnnouncementDetails();
    // Scroll to top when component mounts or URL changes
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getImageUrls = () => {
    if (!announcement || !announcement.images) {
      return [];
    }
    const imageArray = announcement.images.split(',').filter(img => img.trim());
    return imageArray.map(img => getUploadUrl(img));
  };

  const getVideoUrls = () => {
    if (!announcement || !announcement.videos) {
      return [];
    }
    const videoArray = announcement.videos.split(',').filter(vid => vid.trim());
    return videoArray.map(vid => getUploadUrl(vid));
  };

  const getAllMedia = () => {
    const images = getImageUrls().map(url => ({ type: 'image', url }));
    const videos = getVideoUrls().map(url => ({ type: 'video', url }));
    // Videos first, then images
    return [...videos, ...images];
  };

  const handlePrevImage = () => {
    setImageTransitioning(true);
    setImageLoading(true);
    const media = getAllMedia();
    setTimeout(() => {
      setCurrentImageIndex((prev) => (prev - 1 + media.length) % media.length);
      setImageTransitioning(false);
    }, 150);
  };

  const handleNextImage = () => {
    setImageTransitioning(true);
    setImageLoading(true);
    const media = getAllMedia();
    setTimeout(() => {
      setCurrentImageIndex((prev) => (prev + 1) % media.length);
      setImageTransitioning(false);
    }, 150);
  };

  const getWhatsAppLink = () => {
    const phoneNumber = '40743131559'; // Romanian number without + or spaces
    
    // Get the current announcement URL with slug
    const announcementUrl = `${window.location.origin}${getAnnouncementUrl(
      announcement.id, 
      announcement.title,
      announcement.category,
      announcement.subcategory,
      announcement.created_at
    )}`;
    
    // Language-specific messages
    const messages = {
      ro: `Bună ziua! Sunt interesat de anunțul: ${announcement.title} - €${Number(announcement.price).toLocaleString()}. Aș dori mai multe informații.\n\nLink anunț: ${announcementUrl}`,
      en: `Hello! I'm interested in the announcement: ${announcement.title} - €${Number(announcement.price).toLocaleString()}. I would like more information.\n\nAnnouncement link: ${announcementUrl}`
    };
    
    const message = messages[language] || messages.ro;
    const encodedMessage = encodeURIComponent(message);
    return `https://wa.me/${phoneNumber}?text=${encodedMessage}`;
  };

  if (loading) {
    return (
      <div className="announcement-details-page">
        <Navbar />
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !announcement) {
    return (
      <div className="announcement-details-page">
        <Navbar />
        <div className="error-container">
          <h2>{error || 'Announcement not found'}</h2>
          <button onClick={() => navigate('/machines')} className="btn-back">
            <FaArrowLeft /> {t.announcementDetails.backToListings}
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="announcement-details-page">
      <Navbar />
      
      <div className="announcement-container">
        {/* Breadcrumb */}
        <div className="breadcrumb">
          <button onClick={() => navigate('/machines')} className="back-button">
            <FaArrowLeft /> {t.announcementDetails.backToListings}
          </button>
        </div>

        <div className="details-wrapper">
          {/* Left Side - Images & Description */}
          <div className="left-section">
            {/* Image Gallery */}
            <div className="gallery-container">
              {getAllMedia().length > 0 ? (
                <>
                  <div className="main-image-wrapper">
                    {/* Loading Spinner */}
                    {imageLoading && (
                      <div className="media-loading-overlay">
                        <div className="media-loading-spinner"></div>
                      </div>
                    )}
                    
                    {getAllMedia()[currentImageIndex].type === 'image' ? (
                      <img 
                        src={getAllMedia()[currentImageIndex].url} 
                        alt={announcement.title}
                        className={`main-image ${imageTransitioning ? 'transitioning' : ''} ${imageLoading ? 'loading' : ''}`}
                        onLoad={() => setImageLoading(false)}
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/800x600?text=Image+Not+Available';
                          setImageLoading(false);
                        }}
                      />
                    ) : (
                      <video 
                        src={getAllMedia()[currentImageIndex].url}
                        className={`main-image ${imageTransitioning ? 'transitioning' : ''} ${imageLoading ? 'loading' : ''}`}
                        controls
                        autoPlay
                        muted
                        loop
                        playsInline
                        controlsList="nodownload"
                        style={{ objectFit: 'contain' }}
                        onLoadedData={() => setImageLoading(false)}
                        onError={(e) => {
                          console.error('Video load error:', e);
                          setImageLoading(false);
                        }}
                      >
                        Your browser does not support the video tag.
                      </video>
                    )}
                    {getAllMedia().length > 1 && (
                      <>
                        <button className="gallery-nav prev" onClick={handlePrevImage}>
                          <FaChevronLeft />
                        </button>
                        <button className="gallery-nav next" onClick={handleNextImage}>
                          <FaChevronRight />
                        </button>
                        <div className="image-counter">
                          {currentImageIndex + 1} / {getAllMedia().length}
                        </div>
                      </>
                    )}
                  </div>
                  {getAllMedia().length > 1 && (
                    <div className="thumbnails">
                      {getAllMedia().map((media, index) => (
                        <div
                          key={index}
                          className={`thumbnail ${index === currentImageIndex ? 'active' : ''}`}
                          onClick={() => {
                            setImageLoading(true);
                            setCurrentImageIndex(index);
                          }}
                        >
                          {media.type === 'image' ? (
                            <img
                              src={media.url}
                              alt={`Thumbnail ${index + 1}`}
                              onError={(e) => {
                                e.target.src = 'https://via.placeholder.com/100?text=N/A';
                              }}
                            />
                          ) : (
                            <div className="video-thumbnail">
                              <video src={media.url} />
                              <div className="video-play-icon">▶</div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <div className="no-images">
                  <span>{t.announcementDetails.noImagesAvailable}</span>
                </div>
              )}
            </div>

            {/* Description Section */}
            <div className="section-card">
              <h2 className="section-title">{t.announcementDetails.description}</h2>
              <p className="description-text">{announcement.description || t.announcementDetails.noDescription}</p>
            </div>

            {/* Specifications Section */}
            <div className="section-card">
              <h2 className="section-title">{t.announcementDetails.details}</h2>
              <div className="specs-list">
                <div className="spec-row">
                  <div className="spec-label">
                    <FaTags className="spec-icon" />
                    <span>{t.announcementDetails.category}</span>
                  </div>
                  <div className="spec-value">
                    {formatCategoryPath(announcement.category, announcement.subcategory, announcement.subsubcategory) || 'N/A'}
                  </div>
                </div>
                <div className="spec-row">
                  <div className="spec-label">
                    <FaCog className="spec-icon" />
                    <span>{t.announcementDetails.manufacturer}</span>
                  </div>
                  <div className="spec-value">{announcement.manufacturer || 'N/A'}</div>
                </div>
                <div className="spec-row">
                  <div className="spec-label">
                    <FaCog className="spec-icon" />
                    <span>{t.announcementDetails.model}</span>
                  </div>
                  <div className="spec-value">{announcement.model || 'N/A'}</div>
                </div>
                <div className="spec-row">
                  <div className="spec-label">
                    <FaCalendar className="spec-icon" />
                    <span>{t.announcementDetails.year}</span>
                  </div>
                  <div className="spec-value">{announcement.year || 'N/A'}</div>
                </div>
                <div className="spec-row">
                  <div className="spec-label">
                    <FaCog className="spec-icon" />
                    <span>{t.announcementDetails.condition}</span>
                  </div>
                  <div className="spec-value">{announcement.condition ? capitalizeWords(announcement.condition) : 'N/A'}</div>
                </div>
                <div className="spec-row">
                  <div className="spec-label">
                    <FaClock className="spec-icon" />
                    <span>{t.announcementDetails.hours}</span>
                  </div>
                  <div className="spec-value">{announcement.hours ? `${announcement.hours} ${t.announcementDetails.hours.toLowerCase()}` : 'N/A'}</div>
                </div>
                <div className="spec-row">
                  <div className="spec-label">
                    <FaMapMarkerAlt className="spec-icon" />
                    <span>{t.announcementDetails.location}</span>
                  </div>
                  <div className="spec-value">{announcement.location || 'N/A'}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Price & Contact */}
          <div className="right-section">
            {/* Title */}
            <div className="title-card">
              <h1 className="product-title">{announcement.title}</h1>
              <div className="meta-info">
                <span className="meta-item">
                  <FaEye /> {announcement.views || 0} {t.announcementDetails.views}
                </span>
                <span className="meta-item">
                  <FaCalendar /> {formatDate(announcement.created_at)}
                </span>
              </div>
            </div>

            {/* Price Card */}
            <div className="price-card-sticky">
              <div className="price-header">
                <div className="price-badge">
                  <span className="price-badge-text">{t.announcementDetails.price || 'PRICE'}</span>
                </div>
                {(announcement.negotiable === 1 || announcement.negotiable === true) && (
                  <div className="negotiable-tag">
                    <span className="negotiable-icon">💎</span>
                    <span>{t.announcementDetails.negotiable || 'Negotiable'}</span>
                  </div>
                )}
              </div>
              
              <div className="price-display-modern">
                <span className="currency-symbol">€</span>
                <span className="price-amount">{Number(announcement.price).toLocaleString()}</span>
              </div>

              <div className="contact-buttons-group">
                <button className="btn-contact-call">
                  <FaPhone className="btn-icon" />
                  <span className="btn-text">{announcement.contact || t.announcementDetails.contactNotAvailable}</span>
                </button>

                <a 
                  href={getWhatsAppLink()} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="btn-contact-whatsapp"
                >
                  <FaWhatsapp className="btn-icon" />
                  <span className="btn-text">{t.announcementDetails.whatsappButton}</span>
                </a>
              </div>
            </div>

            {/* Location Card */}
            <div className="info-card">
              <h3 className="card-title">{t.announcementDetails.locationTitle}</h3>
              <div className="location-info">
                <FaMapMarkerAlt className="location-icon" />
                <span>{announcement.location || t.announcementDetails.locationNotSpecified}</span>
              </div>
            </div>

            {/* Additional Info Card */}
            <div className="info-card">
              <h3 className="card-title">{t.announcementDetails.additionalInfo}</h3>
              <div className="specs-list">
                {announcement.year && (
                  <div className="spec-row">
                    <div className="spec-label">
                      <FaCalendar className="spec-icon" />
                      <span>{t.announcementDetails.yearOfManufacture}</span>
                    </div>
                    <div className="spec-value">{announcement.year}</div>
                  </div>
                )}
                {announcement.weight && (
                  <div className="spec-row">
                    <div className="spec-label">
                      <FaCog className="spec-icon" />
                      <span>{t.announcementDetails.weight}</span>
                    </div>
                    <div className="spec-value">{announcement.weight} Ton</div>
                  </div>
                )}
                {announcement.power && (
                  <div className="spec-row">
                    <div className="spec-label">
                      <FaCog className="spec-icon" />
                      <span>{t.announcementDetails.power}</span>
                    </div>
                    <div className="spec-value">{announcement.power} HP</div>
                  </div>
                )}
                {announcement.capacity && (
                  <div className="spec-row">
                    <div className="spec-label">
                      <FaCog className="spec-icon" />
                      <span>{t.announcementDetails.capacity}</span>
                    </div>
                    <div className="spec-value">{announcement.capacity} m³</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Modal */}
      {showContactModal && (
        <div className="modal-overlay" onClick={() => setShowContactModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowContactModal(false)}>×</button>
            <h2>Contact Seller</h2>
            <div className="modal-info">
              <div className="modal-row">
                <strong>{t.announcementDetails.contactLabel}:</strong>
                <span>{announcement.contact || t.announcementDetails.notProvided}</span>
              </div>
              <div className="modal-row phone-reveal">
                <strong>{t.announcementDetails.detailsLabel}:</strong>
                <span className="phone-number">{announcement.contact || t.announcementDetails.notProvided}</span>
              </div>
            </div>
            {announcement.contact && (
              <a href={`tel:${announcement.contact}`} className="btn-call">
                <FaPhone /> {t.announcementDetails.callNow}
              </a>
            )}
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}

export default AnnouncementDetails;
